<template>
  <div class="contato-fidelizacao-table">
    <div class="table-actions">
      <Button
        data-testid="contato-fidelizacao-new-btn"
        severity="primary"
        size="small"
        :label="t('Novo contato')"
        icon="pi pi-plus"
        @click="handleNewContact"
        style="margin-top: 20px; margin-bottom: 20px;"
      />
    </div>

    <div class="tab-content">
      <DataTable
        :config="tableConfig"
        :data="tableData"
        :filterConfig="filterConfig"
        :refreshButton="refreshButton"
        @row-click="handleRowClick"
        @edit-click="handleEditClick"
        @button-click="handleButtonClick"
        @refresh="loadData">
      </DataTable>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';
import { useContatoFidelizacao } from '../../composables/useContatoFidelizacao';
import { useConfirmAction } from '../../composables/useConfirmAction';

type Id = number | string;

interface CellParams {
  data: Record<string, unknown>;
  value?: unknown;
}

interface ContatoRow {
  contatofidelizacao_id: Id;
  contatofidelizacao_data_cadastro?: string;
  contatofidelizacao_content?: string;
  [key: string]: unknown;
}

interface Props {
  clienteId: Id;
  clienteNome?: string;
  franquiaId?: Id;
  franquiaNome?: string;
}

const props = withDefaults(defineProps<Props>(), {
  clienteNome: '',
  franquiaId: 0,
  franquiaNome: ''
});

const { t } = useI18n();
const { fetchContatosByClienteId, deleteContatoFidelizacao } = useContatoFidelizacao();
const { confirmAction } = useConfirmAction();

const tableData = ref<ContatoRow[]>([]);

const tableConfig = computed(() => {
  return {
    tableId: 'tabelaContatosFidelizacaoCliente',
    pageLength: 100,
    // Disable filters
    showFilters: false,
    // Disable sorting
    sortable: false,
    columns: tableColumns.value,
    pagination: false,
    stripedRows: true,
    showGridlines: true,
    resizableColumns: true,
    size: 'small',
    heightMode: 'fill-parent'
  };
});

const filterConfig = computed(() => {
  return {
    columns: {
      'contatofidelizacao_id': 'text',
      'contatofidelizacao_data_cadastro': 'date',
      'contatofidelizacao_content': 'text'
    }
  };
});

const refreshButton = computed(() => {
  return {
    show: true,
    text: t('Atualizar')
  };
});

const tableColumns = computed(() => {
  return [
    {
      data: 'contatofidelizacao_id',
      title: t('ID'),
      renderType: 'clickable_id',
      width: 100
    },
    {
      data: 'contatofidelizacao_data_cadastro',
      title: t('Data'),
      width: 180,
      renderType: 'datetime',
      dataType: 'date'
    },
    {
      data: 'contatofidelizacao_content',
      title: t('Conteúdo'),
      cellRenderer: (params: CellParams): string => {
        if (params.value == null || params.value === '') return '';
        const value = String(params.value);
        const maxLength = 200;
        if (value.length > maxLength) {
          return value.substring(0, maxLength) + '...';
        }
        return value;
      }
    },
    {
      data: 'contatofidelizacao_id',
      title: t('Ações'),
      width: 120,
      renderType: 'clickable_button',
      buttonText: t('Excluir'),
      buttonIcon: 'pi pi-trash',
      buttonClass: 'p-button-danger p-button-sm',
      buttonAction: 'delete'
    }
  ];
});

const loadData = async (): Promise<void> => {
  log('ContatoFidelizacaoTable', 'loadData', 'Loading contatos for client', props.clienteId);

  if (!props.clienteId || props.clienteId === 0) {
    tableData.value = [];
    return;
  }

  try {
    const contatos = await fetchContatosByClienteId(Number(props.clienteId));
    tableData.value = (contatos as unknown as ContatoRow[]) || [];
    log('ContatoFidelizacaoTable', 'loadData', 'Loaded records', tableData.value.length);
  } catch (error) {
    log('ContatoFidelizacaoTable', 'loadData', 'Error loading contatos', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar contatos: ') + message);
    tableData.value = [];
  }
};

const handleNewContact = (): void => {
  log('ContatoFidelizacaoTable', 'handleNewContact', 'Opening modal for new contact');
  eventBus.emit('contato-fidelizacao.open-modal-new', {
    franquia_id: Number(props.franquiaId) || 0,
    cliente_id: Number(props.clienteId),
    cliente_nome: props.clienteNome,
    franquia_nome: props.franquiaNome
  });
};

const handleRowClick = (event: { data?: ContatoRow } | unknown): void => {
  const rowData = (event as { data?: ContatoRow } | undefined)?.data;
  log('ContatoFidelizacaoTable', 'handleRowClick', 'Row clicked', rowData?.contatofidelizacao_id || null);
};

const handleEditClick = (contatoId: Id): void => {
  log('ContatoFidelizacaoTable', 'handleEditClick', 'Edit clicked', contatoId);
  editarContato(contatoId);
};

const editarContato = (contatoId: Id): void => {
  log('ContatoFidelizacaoTable', 'editarContato', 'Opening contato', contatoId);
  eventBus.emit('contato-fidelizacao.open-modal-edit', {
    contatofidelizacao_id: contatoId
  });
};

interface ButtonClickData {
  action: string;
  value?: Id;
  data?: ContatoRow;
}

const handleButtonClick = async (buttonData: ButtonClickData): Promise<void> => {
  log('ContatoFidelizacaoTable', 'handleButtonClick', 'Button clicked', buttonData);

  if (buttonData.action === 'delete' && buttonData.value != null) {
    await excluirContato(buttonData.value);
  }
};

const excluirContato = (contatoId: Id): void => {
  log('ContatoFidelizacaoTable', 'excluirContato', 'Deleting contato', contatoId);

  if (!contatoId) {
    log('ContatoFidelizacaoTable', 'excluirContato', 'No contact ID');
    return;
  }

  confirmAction({
    message: t('Tem certeza que deseja excluir este contato?'),
    header: t('Confirmação'),
    onAccept: async () => {
      try {
        log('ContatoFidelizacaoTable', 'excluirContato', 'Deleting contact', contatoId);

        await deleteContatoFidelizacao(Number(contatoId));

        log('ContatoFidelizacaoTable', 'excluirContato', 'Deleted successfully');
        eventBus.emit('globalMessage.success', t('Contato excluído com sucesso!'));
        eventBus.emit('contato-fidelizacao.deleted');

      } catch (error) {
        console.error('Error deleting contato:', error);
        log('ContatoFidelizacaoTable', 'excluirContato', 'Error', error);
        const message = error instanceof Error ? error.message : String(error);
        eventBus.emit('globalMessage.error', message);
      }
    }
  });
};

const handleContatoSaved = (): void => {
  log('ContatoFidelizacaoTable', 'eventBus:saved', 'Contact saved, refreshing table');
  loadData();
};

const handleContatoDeleted = (): void => {
  log('ContatoFidelizacaoTable', 'eventBus:deleted', 'Contact deleted, refreshing table');
  loadData();
};

// Lifecycle
onMounted(() => {
  log('ContatoFidelizacaoTable', 'mounted', 'Component mounted');
  loadData();

  // Listen for save/delete events to refresh table
  eventBus.on('contato-fidelizacao.saved', handleContatoSaved);
  eventBus.on('contato-fidelizacao.deleted', handleContatoDeleted);
});

onBeforeUnmount(() => {
  log('ContatoFidelizacaoTable', 'beforeUnmount', 'Cleaning up event listeners');
  eventBus.off('contato-fidelizacao.saved', handleContatoSaved);
  eventBus.off('contato-fidelizacao.deleted', handleContatoDeleted);
});

// Expose methods for parent components
defineExpose({
  loadData
});
</script>

<style scoped>
.contato-fidelizacao-table {
  padding: 15px;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.table-actions {
  margin-bottom: 10px;
}

.tab-content {
  flex: 1;
  min-height: 300px;
  display: flex;
  flex-direction: column;
}
</style>

